import { defineStore } from 'pinia'
import { ref } from 'vue'

export type NotificationKind = 'info' | 'success' | 'warning' | 'error'

export interface AppNotification {
  id: number
  kind: NotificationKind
  message: string
}

/**
 * Transient messages shown to the user — a saved RSVP, a claim that was just
 * taken by another family.
 *
 * Screen-crossing by nature: an action on one screen often navigates before its
 * result is shown, which is why this is a store rather than component state.
 */
export const useNotificationStore = defineStore('notification', () => {
  const items = ref<AppNotification[]>([])
  let nextId = 1

  function push(kind: NotificationKind, message: string): number {
    const id = nextId++
    items.value.push({ id, kind, message })
    return id
  }

  function dismiss(id: number): void {
    items.value = items.value.filter((n) => n.id !== id)
  }

  const notify = {
    info: (m: string) => push('info', m),
    success: (m: string) => push('success', m),
    warning: (m: string) => push('warning', m),
    error: (m: string) => push('error', m),
  }

  return { items, push, dismiss, notify }
})
